import { Card, CardBody, CardHeader } from '@/components/Card'
import { CopyButton } from '@/components/CopyButton'
import { ErrorNotice } from '@/components/ErrorNotice'
import { Skeleton } from '@/components/Skeleton'
import { useReferral } from '@/hooks/useAccount'
import { useAuth } from '@/hooks/useAuth'

/**
 * The user's invite link.
 *
 * The approval requirement is stated plainly rather than buried: someone who
 * hands this to a friend should know up front that it does not let them
 * straight in, so nobody is left wondering why their friend "signed up" and
 * still cannot watch anything.
 */
export function ReferralCard() {
  const { user } = useAuth()
  const referral = useReferral()

  // Someone still in the queue themselves has no business inviting others.
  if (user?.approvalState === 'pending') return null

  return (
    <Card>
      <CardHeader
        title="Invite someone"
        description="Share this link. Whoever uses it picks their own username and password, then waits for an admin to let them in."
      />
      <CardBody>
        {referral.isPending ? (
          <Skeleton className="h-11 w-full" />
        ) : referral.isError ? (
          <ErrorNotice error={referral.error} onRetry={() => void referral.refetch()} />
        ) : referral.data ? (
          <div className="space-y-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <p className="flex min-h-11 flex-1 items-center overflow-x-auto rounded-[3px] border border-line-strong bg-elevated px-3 font-mono text-[11px] text-muted [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {referral.data.url}
              </p>
              <CopyButton value={referral.data.url} label="Copy link" />
            </div>

            <p className="flex flex-wrap items-baseline gap-x-2 font-mono text-[11px] text-faint">
              <span>
                used {referral.data.usedCount ?? 0}
                {referral.data.maxUses ? `/${referral.data.maxUses}` : ''}
              </span>
              <span aria-hidden>·</span>
              <span className="text-warn">needs your approval</span>
            </p>
          </div>
        ) : null}
      </CardBody>
    </Card>
  )
}
